import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconChevronLeft,
  IconMedicalCross,
  IconFolders,
  IconClipboardList,
  IconUsersGroup,
  IconWifiOff,
  IconBrandWhatsapp,
  IconPillFilled,
  IconActivityHeartbeat,
  IconHistory,
  IconCalendarTime,
  IconVaccine,
  IconFlask,
  IconShieldLock,
  IconCheck,
  IconArrowNarrowRight,
  IconFile,
} from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import styles from "./HowMaatraWorks.module.css";

const HowMaatraWorks: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <IconChevronLeft size={16} color="#2a2a2a" />
          <span>{t("auth.howMaatraWorks.back")}</span>
        </button>

        <div className={styles.logoCircle}>
          <MaatraLogo fill="#fff" size={37} />
        </div>

        <p className={styles.tagline}>{t("auth.howMaatraWorks.tagline")}</p>

        <h1 className={styles.headline}>
          <span className={styles.headlineDark}>
            {t("auth.howMaatraWorks.headline1")}
          </span>
          <span className={styles.headlineLight}>
            {t("auth.howMaatraWorks.headline2")}
          </span>
          <span className={styles.headlineDark}>
            {t("auth.howMaatraWorks.headline3")}
          </span>
        </h1>
      </div>

      {/* ── Scrollable content ─────────────────────────────────── */}
      <div className={styles.scrollContent}>
        {/* Card 1 — Emergency Card */}
        <div className={`${styles.card} ${styles.cardRed}`}>
          <div className={`${styles.badge} ${styles.badgeRed}`}>
            <IconMedicalCross size={16} color="#8b3166" />
            <span>{t("auth.howMaatraWorks.emergencyCard.badge")}</span>
          </div>
          <p className={`${styles.cardTitle} ${styles.cardTitleRed}`}>
            {t("auth.howMaatraWorks.emergencyCard.title")}
          </p>
          <div className={`${styles.innerCard} ${styles.innerCardWhite}`}>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>
                {t("auth.howMaatraWorks.emergencyCard.bloodGroup")}
              </span>
              <span className={styles.dataValue}>
                {t("auth.howMaatraWorks.emergencyCard.bloodGroupValue")}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>
                {t("auth.howMaatraWorks.emergencyCard.allergies")}
              </span>
              <span className={styles.dataValue}>
                {t("auth.howMaatraWorks.emergencyCard.allergiesValue")}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>
                {t("auth.howMaatraWorks.emergencyCard.chronicCondition")}
              </span>
              <span className={styles.dataValue}>
                {t("auth.howMaatraWorks.emergencyCard.chronicConditionValue")}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>
                {t("auth.howMaatraWorks.emergencyCard.status")}
              </span>
              <span className={styles.offlineBadge}>
                <IconWifiOff size={14} color="#45168c" />
                <span>
                  {t("auth.howMaatraWorks.emergencyCard.worksOffline")}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Card 2 — Health Document Vault */}
        <div className={`${styles.card} ${styles.cardPurple}`}>
          <div className={`${styles.badge} ${styles.badgePurple}`}>
            <IconFolders size={16} color="#4c189a" />
            <span>{t("auth.howMaatraWorks.docVault.badge")}</span>
          </div>
          <p className={`${styles.cardTitle} ${styles.cardTitlePurple}`}>
            {t("auth.howMaatraWorks.docVault.title")}
          </p>
          <div className={styles.vaultFlow}>
            <div className={styles.whatsappBadge}>
              <IconBrandWhatsapp size={16} color="#fff" />
            </div>
            <div
              className={`${styles.innerCard} ${styles.innerCardWhite} ${styles.vaultFileCard}`}
            >
              <IconFile size={16} color="#45168c" />
              <span className={styles.vaultFileName}>
                {t("auth.howMaatraWorks.docVault.scanFile")}
              </span>
            </div>
            <IconArrowNarrowRight
              size={16}
              color="#474747"
              className={styles.vaultArrow}
            />
            <div
              className={`${styles.innerCard} ${styles.innerCardWhite} ${styles.vaultResultCard}`}
            >
              <span className={styles.vaultOrganisedLabel}>
                {t("auth.howMaatraWorks.docVault.organised")}
              </span>
              <div className={styles.vaultResultRow}>
                <span className={styles.vaultResultValue}>
                  {t("auth.howMaatraWorks.docVault.dadPerson")}
                </span>
                <span className={styles.vaultResultDot}>·</span>
                <span className={styles.vaultResultValue}>
                  {t("auth.howMaatraWorks.docVault.dadReportsLabel")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3 — Health Summary & Trends */}
        <div className={`${styles.card} ${styles.cardGreen}`}>
          <div className={`${styles.badge} ${styles.badgeGreen}`}>
            <IconClipboardList size={16} color="#217b57" />
            <span>{t("auth.howMaatraWorks.healthSummary.badge")}</span>
          </div>
          <p className={`${styles.cardTitle} ${styles.cardTitleGreen}`}>
            {t("auth.howMaatraWorks.healthSummary.title")}
          </p>
          <div className={styles.summaryList}>
            <div className={styles.summaryRow}>
              <div className={styles.summaryRowLeft}>
                <IconPillFilled size={17} color="#134933" />
                <span className={styles.summaryRowLabel}>
                  {t("auth.howMaatraWorks.healthSummary.medications")}
                </span>
              </div>
              <span className={styles.summaryRowValue}>
                {t("auth.howMaatraWorks.healthSummary.medicationsValue")}
              </span>
            </div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryRow}>
              <div className={styles.summaryRowLeft}>
                <IconActivityHeartbeat size={17} color="#134933" />
                <span className={styles.summaryRowLabel}>
                  {t("auth.howMaatraWorks.healthSummary.hba1c")}
                </span>
              </div>
              <span
                className={`${styles.summaryRowValue} ${styles.valueGreen}`}
              >
                {t("auth.howMaatraWorks.healthSummary.hba1cValue")}
              </span>
            </div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryRow}>
              <div className={styles.summaryRowLeft}>
                <IconHistory size={17} color="#134933" />
                <span className={styles.summaryRowLabel}>
                  {t("auth.howMaatraWorks.healthSummary.history")}
                </span>
              </div>
              <span className={styles.summaryRowValue}>
                {t("auth.howMaatraWorks.healthSummary.historyValue")}
              </span>
            </div>
          </div>
        </div>

        {/* Card 4 — Family Access */}
        <div className={`${styles.card} ${styles.cardOrange}`}>
          <div className={`${styles.badge} ${styles.badgeOrange}`}>
            <IconUsersGroup size={16} color="#623b1b" />
            <span>{t("auth.howMaatraWorks.familyAccess.badge")}</span>
          </div>
          <p className={`${styles.cardTitle} ${styles.cardTitleOrange}`}>
            {t("auth.howMaatraWorks.familyAccess.title")}
          </p>
          <div className={styles.familyList}>
            <div className={styles.familyRow}>
              <div className={styles.familyMember}>
                <div className={`${styles.avatar} ${styles.avatarFilled}`}>
                  <span>RD</span>
                </div>
                <div className={styles.memberInfo}>
                  <span className={styles.memberName}>
                    {t("auth.howMaatraWorks.familyAccess.member1Name")}
                  </span>
                  <span className={styles.memberLocation}>
                    {t("auth.howMaatraWorks.familyAccess.member1Location")}
                  </span>
                </div>
              </div>
              <span className={styles.accessBadge}>
                <IconCheck size={14} color="#45168c" />
                <span>
                  {t("auth.howMaatraWorks.familyAccess.member1Access")}
                </span>
              </span>
            </div>
            <div className={styles.familyRow}>
              <div className={styles.familyMember}>
                <div className={`${styles.avatar} ${styles.avatarOutline}`}>
                  <span>SD</span>
                </div>
                <div className={styles.memberInfo}>
                  <span className={styles.memberName}>
                    {t("auth.howMaatraWorks.familyAccess.member2Name")}
                  </span>
                  <span className={styles.memberLocation}>
                    {t("auth.howMaatraWorks.familyAccess.member2Location")}
                  </span>
                </div>
              </div>
              <span className={styles.accessBadge}>
                <IconCheck size={14} color="#45168c" />
                <span>
                  {t("auth.howMaatraWorks.familyAccess.member2Access")}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Also built into Maatra */}
        <div className={styles.alsoBuilt}>
          <p className={styles.alsoBuiltHeading}>
            {t("auth.howMaatraWorks.alsoBuilt.heading")}
          </p>
          <div className={styles.alsoBuiltGrid}>
            <div className={styles.miniCard}>
              <div className={`${styles.miniIcon} ${styles.miniIconPurple}`}>
                <IconCalendarTime size={17} color="#45168c" />
              </div>
              <span className={styles.miniLabel}>
                {t("auth.howMaatraWorks.alsoBuilt.reminders")}
              </span>
            </div>
            <div className={styles.miniCard}>
              <div className={`${styles.miniIcon} ${styles.miniIconPurple}`}>
                <IconVaccine size={17} color="#45168c" />
              </div>
              <span className={styles.miniLabel}>
                {t("auth.howMaatraWorks.alsoBuilt.vaccination")}
              </span>
            </div>
            <div className={styles.miniCard}>
              <div className={`${styles.miniIcon} ${styles.miniIconPink}`}>
                <IconActivityHeartbeat size={17} color="#8b3166" />
              </div>
              <span className={styles.miniLabel}>
                {t("auth.howMaatraWorks.alsoBuilt.vitals")}
              </span>
            </div>
            <div className={styles.miniCard}>
              <div className={`${styles.miniIcon} ${styles.miniIconPink}`}>
                <IconFlask size={17} color="#8b3166" />
              </div>
              <span className={styles.miniLabel}>
                {t("auth.howMaatraWorks.alsoBuilt.labTrends")}
              </span>
            </div>
          </div>
        </div>

        {/* Security card */}
        <div className={styles.securityCard}>
          <div className={styles.securityHeader}>
            <IconShieldLock size={16} color="#45168c" />
            <span className={styles.securityTitle}>
              {t("auth.howMaatraWorks.security.title")}
            </span>
          </div>
          {[
            t("auth.howMaatraWorks.security.point1"),
            t("auth.howMaatraWorks.security.point2"),
            t("auth.howMaatraWorks.security.point3"),
            t("auth.howMaatraWorks.security.point4"),
          ].map((point) => (
            <div key={point} className={styles.securityRow}>
              <IconCheck size={16} color="#45168c" />
              <span className={styles.securityPoint}>{point}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Floating CTA ───────────────────────────────────────── */}
      <div className={styles.floatingCta}>
        <MButton
          variant="filled"
          size="lg"
          onClick={() => navigate("/register/consent")}
        >
          {t("auth.howMaatraWorks.createAccount")}
        </MButton>
      </div>
    </div>
  );
};

export default HowMaatraWorks;
